import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
    title: string;
    value: string | number;
    subtitle: string;
    subtitleValue?: string | number;
    icon: LucideIcon;
    iconColor: string;
    trend?: 'up' | 'down' | 'neutral';
    loading?: boolean;
}

export function StatCard({
    title,
    value,
    subtitle,
    subtitleValue,
    icon: Icon,
    iconColor,
    trend,
    loading
}: StatCardProps) {
    if (loading) {
        return (
            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center gap-2.5">
                        <Skeleton className="h-9 w-9 rounded-xl shrink-0" />
                        <Skeleton className="h-6 w-14" />
                    </div>
                    <div className="mt-2.5 space-y-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-3 w-28" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Icon chip sits with the value on the top row (the number reads first),
    // the title names it small underneath, and the subtitle/trend sits
    // smallest at the bottom — matches the StatCard tile pattern used across
    // the Dashboard, Analytics and Cloud Assets pages.
    return (
        <Card className="overflow-hidden">
            <CardContent className="p-4">
                <div className="flex items-center gap-2.5">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-xl shrink-0 ${iconColor}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                    <p className="text-xl font-bold tracking-tight text-foreground tabular-nums leading-none">{value}</p>
                </div>
                <div className="mt-2.5">
                    <p className="text-xs font-medium text-muted-foreground">{title}</p>
                    <div className="flex items-center gap-1.5 text-[11px] mt-1">
                        <span className="text-muted-foreground">{subtitle}</span>
                        {subtitleValue !== undefined && (
                            <span className={
                                trend === 'up' ? 'text-success font-semibold' :
                                    trend === 'down' ? 'text-destructive font-semibold' :
                                        'text-muted-foreground font-semibold'
                            }>
                                {subtitleValue}
                            </span>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
